import React from 'react';

/**
 * Compact icon-only row for the 4 supply-control categories. Each icon
 * appears only when its category is > 1% of supply. Tooltips carry the
 * exact percentages. Used inline on list views (trending rows, home
 * page list cards) where the full pill row from the detail page would
 * be too heavy.
 *
 * Color/icon mapping mirrors the detail-page pills:
 *   🔒 lock    (locked, green)   — Streamflow vesting / lock contracts
 *   💎 gem     (staked, yellow)  — Tibane Labs ChiefStaker
 *   🛡 shield  (multisig, amber) — Squads V4 multisig vaults (threshold ≥ 2)
 *   🔥 fire    (burned, orange) — incinerator + mint-authority burn
 */
export const SupplyControlIcons: React.FC<{
    locked?: number;
    staked?: number;
    multisig?: number;
    burned?: number;
    /** Multisig detail for the tooltip — vault count, plus threshold/members
     *  when there's only one vault (richer "X-of-Y" display). */
    multisigVaultCount?: number;
    multisigThreshold?: number;
    multisigMemberCount?: number;
    /** Minimum % to show an icon (default 1). */
    threshold?: number;
}> = ({
    locked, staked, multisig, burned,
    multisigVaultCount, multisigThreshold, multisigMemberCount,
    threshold = 1,
}) => {
    const multisigTitle = (() => {
        if (multisig == null || multisig <= threshold) return '';
        const pct = `${multisig.toFixed(1)}% multisig`;
        if (multisigVaultCount === 1 && multisigThreshold && multisigMemberCount) {
            return `${pct} (${multisigThreshold}-of-${multisigMemberCount})`;
        }
        if (multisigVaultCount && multisigVaultCount > 1) {
            return `${pct} across ${multisigVaultCount} vaults`;
        }
        return pct;
    })();
    const items = [
        { value: locked,   icon: 'fa-lock',           color: 'text-green-400',  title: locked != null   ? `${locked.toFixed(1)}% locked`   : '', label: 'locked' },
        { value: staked,   icon: 'fa-gem',            color: 'text-yellow-300', title: staked != null   ? `${staked.toFixed(1)}% staked`   : '', label: 'staked' },
        { value: multisig, icon: 'fa-shield-halved',  color: 'text-amber-400',  title: multisigTitle,                                          label: 'multisig' },
        { value: burned,   icon: 'fa-fire',           color: 'text-orange-300', title: burned != null   ? `${burned.toFixed(1)}% burned`   : '', label: 'burned' },
    ];
    const visible = items.filter(i => (i.value ?? 0) > threshold);
    if (visible.length === 0) return null;
    return (
        <span className="inline-flex items-center gap-1 ml-1.5">
            {visible.map(i => (
                <span key={i.label} className={i.color} title={i.title}>
                    <i className={`fas ${i.icon} text-[10px]`} />
                </span>
            ))}
        </span>
    );
};
